import { logger } from "@utils/logger/logger";

const RECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?render=${ReCaptchaChallengeKey}`;

export default class CaptchaService {
	private static retryCount: number = 0;
	private static maxWaitTime: number = 5000;

	private static loadRecaptchaScript(): void {
		const existingScript = Array.from(document.scripts).find((script) =>
			script.src.includes("https://www.google.com/recaptcha/api.js")
		);

		if (existingScript) {
			return;
		}

		const script = document.createElement("script");
		script.src = RECAPTCHA_SRC;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	}

	private static waitForGrecaptchaToLoad(): Promise<boolean> {
		return new Promise((resolve) => {
			const pollInterval = 100;
			let elapsedTime = 0;

			const checkIfLoaded = () => {
				if ((window as any).grecaptcha) {
					resolve(true);
				} else if (elapsedTime >= this.maxWaitTime) {
					resolve(false);
				} else {
					elapsedTime += pollInterval;
					setTimeout(checkIfLoaded, pollInterval);
				}
			};

			checkIfLoaded();
		});
	}

	private static async getCaptchaToken(
		action: string
	): Promise<string | null> {
		try {
			this.loadRecaptchaScript();

			const isGrecaptchaLoaded = await this.waitForGrecaptchaToLoad();

			if (!isGrecaptchaLoaded) {
				logger.logError(
					"reCaptcha not loaded after waiting period of 5 seconds",
					{
						extra: {
							grecaptchaExists: false,
							userAgent: navigator.userAgent,
							timestamp: new Date().toISOString(),
							action,
						},
						tags: {
							section: "captcha-load-error",
							component: "CaptchaService.ts",
						},
					}
				);
				return null;
			}

			const token: string = await new Promise<string>(
				(resolve, reject) => {
					(window as any).grecaptcha.ready(() => {
						(window as any).grecaptcha
							.execute(ReCaptchaChallengeKey, { action: action })
							.then(resolve)
							.catch(reject);
					});
				}
			);

			return token || null;
		} catch (error) {
			logger.logError("Error executing reCAPTCHA", {
				extra: {
					error: (error as any)?.message,
					stack: (error as any)?.stack,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
					action,
				},
				tags: {
					section: "captcha-execute-error",
					component: "CaptchaService.ts",
				},
			});
			return null;
		}
	}

	static async executeCaptcha(action: string): Promise<string | null> {
		try {
			const maxRetryCount: number = 3;
			const retryDelay = [500, 750, 1000];

			while (this.retryCount < maxRetryCount) {
				const recaptchaToken = await this.getCaptchaToken(action);

				if (recaptchaToken?.includes("browser-error")) {
					const delay = retryDelay[this.retryCount] || 1000;

					logger.logError("reCAPTCHA returned browser-error token.", {
						extra: {
							token: recaptchaToken,
							retryCount: this.retryCount,
							delay,
							action,
							userAgent: navigator.userAgent,
						},
						tags: {
							section: "captcha-browser-error",
							component: "CaptchaService.ts",
						},
					});

					this.retryCount++;

					await new Promise((resolve) => setTimeout(resolve, delay));
				} else {
					this.retryCount = 0;
					return recaptchaToken;
				}
			}

			this.retryCount = 0;
			return null;
		} catch (error) {
			logger.logError("Unexpected error in executeCaptcha", {
				extra: {
					error: (error as any)?.message,
					stack: (error as any)?.stack,
					action,
					userAgent: navigator.userAgent,
				},
				tags: {
					section: "captcha-unexpected",
					component: "CaptchaService.ts",
				},
			});
			return null;
		}
	}
}
